import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles, Copy, ChevronDown, ChevronUp, AlertCircle, CheckCircle2,
  Loader2, Save, FileText, Trash2, Eye,
} from 'lucide-react';
import { SIGNAL_PROVIDERS } from './signalProviders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['WIN', 'LOSS', 'BREAKEVEN'];
const POSITION_OPTIONS = ['BUY', 'SELL'];
const SIGNAL_OPTIONS = [
  { value: 'mapping', label: 'Mapping' },
  { value: 'livetrade', label: 'Livetrade' },
  { value: 'others', label: 'Others' },
  { value: '__blank__', label: '(blank)' },
];
// Radix <Select /> does not allow empty-string values, so we translate a
// sentinel "__blank__" to actual empty string when saving to the API.
const normaliseSignal = (v) => (v === '__blank__' ? '' : v || '');

export default function QuickLogTemplateModal({ open, onClose, ib, onSaved, initialText, autoParse }) {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTemplateGuide, setShowTemplateGuide] = useState(false);
  const [sampleData, setSampleData] = useState(null);
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideIbId, setOverrideIbId] = useState('');
  const [postTo, setPostTo] = useState('both');
  const [defaultSignal, setDefaultSignal] = useState('mapping');
  const [defaultProvider, setDefaultProvider] = useState('');
  const [activeChannels, setActiveChannels] = useState([]);

  const token = localStorage.getItem('token');
  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!open) return;
    // Reset state when opening. If a caller (e.g., "Sync & Select" flow)
    // passes initialText, prefill the textarea AND optionally auto-run parse
    // so admin can jump straight to review.
    setText(initialText || '');
    setParsed(null);
    setOverrideDate('');
    setOverrideIbId(ib?.id || '');
    setPostTo('both');
    setDefaultSignal('mapping');
    setDefaultProvider('');
    setShowTemplateGuide(false);
    // Load sample & channel list
    axios.get(`${API}/operations/template/sample`, h).then((r) => setSampleData(r.data)).catch(() => {});
    axios.get(`${API}/active-channels`, h).then((r) => setActiveChannels(r.data?.channels || [])).catch(() => {});
    // Auto-parse when text was prefilled from the sync-preview flow
    if (initialText && autoParse) {
      setTimeout(async () => {
        setParsing(true);
        try {
          const res = await axios.post(`${API}/operations/template/parse`, { text: initialText }, h);
          setParsed(res.data);
          setOverrideDate(res.data.header?.date || '');
        } catch (e) {
          toast.error('Auto-parse failed — click Parse Preview manually');
        } finally { setParsing(false); }
      }, 200);
    }
  }, [open, ib, h, initialText, autoParse]);

  const handleParse = async () => {
    if (!text.trim()) {
      toast.error('Paste your daily summary text first');
      return;
    }
    setParsing(true);
    try {
      const res = await axios.post(`${API}/operations/template/parse`, { text }, h);
      setParsed(res.data);
      // Default overrides from parsed values
      setOverrideDate(res.data.header?.date || '');
      if (res.data.header?.channel_match_id) {
        setOverrideIbId(res.data.header.channel_match_id);
      }
      if (res.data.errors?.length) toast.error(res.data.errors[0]);
      else toast.success(`Parsed ${res.data.trades.length} trade(s)`);
    } catch (e) {
      toast.error('Parse failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setParsing(false);
    }
  };

  const handleEditTrade = (idx, field, value, groupIdx = null) => {
    const next = { ...parsed };
    if (groupIdx != null && Array.isArray(next.groups)) {
      // Multi-date mode — edit within the specific group
      next.groups = next.groups.map((g, i) => {
        if (i !== groupIdx) return g;
        const newTrades = [...g.trades];
        newTrades[idx] = { ...newTrades[idx], [field]: value };
        if (field === 'status') {
          const p = Number(newTrades[idx].pips) || 0;
          if (value === 'LOSS' && p > 0) newTrades[idx].pips = -Math.abs(p);
          if (value === 'WIN' && p < 0) newTrades[idx].pips = Math.abs(p);
          if (value === 'BREAKEVEN') newTrades[idx].pips = 0;
        }
        if (field === 'pips') {
          const status = newTrades[idx].status;
          const p = Number(value) || 0;
          if (status === 'LOSS' && p > 0) newTrades[idx].pips = -p;
          else if (status === 'WIN' && p < 0) newTrades[idx].pips = Math.abs(p);
          else newTrades[idx].pips = p;
        }
        newTrades[idx].valid = !!(newTrades[idx].pair && newTrades[idx].position && newTrades[idx].status);
        return { ...g, trades: newTrades };
      });
    } else {
      next.trades = [...parsed.trades];
      next.trades[idx] = { ...next.trades[idx], [field]: value };
      if (field === 'status') {
        const p = Number(next.trades[idx].pips) || 0;
        if (value === 'LOSS' && p > 0) next.trades[idx].pips = -Math.abs(p);
        if (value === 'WIN' && p < 0) next.trades[idx].pips = Math.abs(p);
        if (value === 'BREAKEVEN') next.trades[idx].pips = 0;
      }
      if (field === 'pips') {
        const status = next.trades[idx].status;
        const p = Number(value) || 0;
        if (status === 'LOSS' && p > 0) next.trades[idx].pips = -p;
        else if (status === 'WIN' && p < 0) next.trades[idx].pips = Math.abs(p);
        else next.trades[idx].pips = p;
      }
      next.trades[idx].valid = !!(next.trades[idx].pair && next.trades[idx].position && next.trades[idx].status);
    }
    setParsed(next);
  };

  const handleRemoveTrade = (idx, groupIdx = null) => {
    const next = { ...parsed };
    if (groupIdx != null && Array.isArray(next.groups)) {
      next.groups = next.groups.map((g, i) =>
        i === groupIdx ? { ...g, trades: g.trades.filter((_, j) => j !== idx) } : g
      );
    } else {
      next.trades = parsed.trades.filter((_, i) => i !== idx);
    }
    setParsed(next);
  };

  // Apply default signal to ALL rows in one click
  const applyDefaultSignalToAll = () => {
    if (!parsed) return;
    const next = { ...parsed };
    if (Array.isArray(next.groups) && next.groups.length > 1) {
      next.groups = next.groups.map(g => ({
        ...g,
        trades: g.trades.map(t => ({ ...t, signal_generated: defaultSignal })),
      }));
    } else {
      next.trades = (parsed.trades || []).map(t => ({ ...t, signal_generated: defaultSignal }));
    }
    setParsed(next);
    toast.success(`Signal Generated = "${defaultSignal || 'blank'}" applied to all rows`);
  };

  const applyProviderToAll = (p) => {
    setDefaultProvider(p);
    if (!parsed) return;
    const next = { ...parsed };
    if (Array.isArray(next.groups) && next.groups.length > 1) {
      next.groups = next.groups.map(g => ({ ...g, trades: g.trades.map(t => ({ ...t, signal_provider: p })) }));
    } else {
      next.trades = (parsed.trades || []).map(t => ({ ...t, signal_provider: p }));
    }
    setParsed(next);
  };

  const isMultiDate = Array.isArray(parsed?.groups) && parsed.groups.length > 1;
  const validTrades = parsed?.trades?.filter((t) => t.valid && t.pair && t.position && t.status) || [];
  const computedTotal = validTrades.reduce((s, t) => s + (Number(t.pips) || 0), 0);

  // In multi-date mode, save is enabled if every group has ≥1 valid trade.
  // In single-date mode, need overrideDate + overrideIbId + ≥1 valid trade.
  const isValidTrade = (t) => t.valid && t.pair && t.position && t.status;
  const allValid = isMultiDate ? parsed.groups.flatMap(g => (g.trades || []).filter(isValidTrade)) : validTrades;
  const missingProvider = allValid.filter(t => !t.signal_provider).length;
  const canSave = parsed && missingProvider === 0 && (
    isMultiDate
      ? overrideIbId && parsed.groups.every(g => g.trades?.some(isValidTrade))
      : (validTrades.length > 0 && overrideDate && overrideIbId)
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const buildPayload = (dateIso, tradesList) => ({
        ib_id: overrideIbId,
        date: dateIso,
        post_to: postTo,
        trades: tradesList.map((t) => ({
          pair: t.pair,
          position: t.position,
          status: t.status,
          pips: t.pips,
          post_to: t.post_to || postTo,
          signal_generated: t.signal_generated || defaultSignal || '',
          signal_provider: t.signal_provider,
        })),
      });
      if (isMultiDate) {
        // Fire one save per date-group. If any fails we stop and surface the
        // error — user can inspect / retry.
        const results = [];
        for (const g of parsed.groups) {
          const validGroupTrades = g.trades.filter(t => t.valid && t.pair && t.position && t.status);
          if (validGroupTrades.length === 0) continue;
          const res = await axios.post(
            `${API}/operations/template/save`,
            buildPayload(g.date, validGroupTrades),
            h,
          );
          results.push(res.data);
        }
        const totalAdded = results.reduce((s, r) => s + (r.added_count || 0), 0);
        toast.success(`Saved ${totalAdded} trade(s) across ${results.length} day(s)`);
        if (onSaved) onSaved({ multi: true, results });
      } else {
        const res = await axios.post(
          `${API}/operations/template/save`,
          buildPayload(overrideDate, validTrades),
          h,
        );
        toast.success(`Saved ${res.data.added_count} trade(s) → ${res.data.ib_name} on ${res.data.date}`);
        if (onSaved) onSaved(res.data);
      }
      onClose();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const copyTemplate = () => {
    if (sampleData?.sample) {
      navigator.clipboard.writeText(sampleData.sample);
      toast.success('Template copied to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl bg-[hsl(var(--card))] border-[hsl(var(--border))] max-h-[90vh] overflow-y-auto" data-testid="quick-log-template-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-amber-400" />
            Quick Log from Template
          </DialogTitle>
          <DialogDescription className="text-xs">
            Paste your daily performance summary — sistem auto-parse jadi multiple trade rows.
          </DialogDescription>
        </DialogHeader>

        {/* Show Template Guide */}
        <div className="bg-[hsl(var(--surface-elevated))]/40 border border-[hsl(var(--border))] rounded-lg p-3">
          <button
            onClick={() => setShowTemplateGuide(!showTemplateGuide)}
            className="flex items-center gap-2 text-xs font-semibold text-amber-300 hover:text-amber-200 w-full"
            data-testid="toggle-template-guide"
          >
            <FileText size={12} />
            <span>Standard Template Format</span>
            {showTemplateGuide ? <ChevronUp size={12} className="ml-auto" /> : <ChevronDown size={12} className="ml-auto" />}
          </button>
          {showTemplateGuide && sampleData && (
            <div className="mt-3 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-semibold text-[hsl(var(--text-secondary))]">Sample (copy and edit):</span>
                  <Button size="sm" variant="ghost" onClick={copyTemplate} className="h-6 text-[10px] text-amber-400" data-testid="copy-template-btn">
                    <Copy size={10} className="mr-1" /> Copy
                  </Button>
                </div>
                <pre className="text-[11px] bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded p-2 text-emerald-300 font-mono whitespace-pre-wrap">
{sampleData.sample}
                </pre>
              </div>
              <div>
                <p className="text-[10px] text-[hsl(var(--text-muted))] mb-1.5">Pair shortcuts supported:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(sampleData.pair_aliases).slice(0, 16).map(([canon, aliases]) => (
                    <Badge key={canon} className="text-[9px] bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))]" title={`${aliases.join(', ')} → ${canon}`}>
                      {aliases[0]} → {canon}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] text-[hsl(var(--text-muted))] mb-1.5">Status codes: <code className="text-amber-300">TP</code>→WIN · <code className="text-amber-300">SL</code>→LOSS · <code className="text-amber-300">BE</code>→BREAKEVEN</p>
              </div>
            </div>
          )}
        </div>

        {/* Paste textarea */}
        <div>
          <label className="text-xs font-semibold text-[hsl(var(--text-secondary))] mb-1.5 block">Paste your daily summary</label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="FIRA WW - 20/1/2026&#10;&#10;1. GOLD - BUY - TP - 100PIP&#10;2. GOLD - SELL - TP - 200PIP&#10;3. GOLD - SELL - SL - 50PIP&#10;&#10;TOTAL PIPS GAIN - 250 PIP"
            className="min-h-[180px] bg-[hsl(var(--surface))] border-[hsl(var(--border))] font-mono text-xs"
            data-testid="quick-log-textarea"
          />
          <div className="flex justify-end mt-2">
            <Button onClick={handleParse} disabled={parsing || !text.trim()} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="parse-btn">
              {parsing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Eye size={14} className="mr-2" />}
              Parse Preview
            </Button>
          </div>
        </div>

        {/* Warnings & Errors */}
        {parsed && (parsed.errors?.length > 0 || parsed.warnings?.length > 0) && (
          <div className="space-y-1">
            {parsed.errors?.map((e, i) => (
              <div key={`err-${i}`} className="bg-red-500/10 border border-red-500/20 rounded p-2 text-[11px] text-red-300 flex items-start gap-2">
                <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {e}
              </div>
            ))}
            {parsed.warnings?.map((w, i) => (
              <div key={`warn-${i}`} className="bg-amber-500/10 border border-amber-500/20 rounded p-2 text-[11px] text-amber-300 flex items-start gap-2">
                <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {w}
              </div>
            ))}
          </div>
        )}

        {/* Preview Table(s) */}
        {parsed && (parsed.trades?.length > 0 || (isMultiDate && parsed.groups?.some(g => g.trades?.length))) && (
          <div className="space-y-3" data-testid="preview-section">
            {isMultiDate && (
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded p-2 text-[11px] text-cyan-200 flex items-start gap-2" data-testid="multi-date-banner">
                <Sparkles size={12} className="flex-shrink-0 mt-0.5" />
                <div>
                  Multi-date summary detected — <b>{parsed.groups.length}</b> date group{parsed.groups.length !== 1 ? 's' : ''} will be saved separately.
                  Pick channel once at the bottom; each date auto-saves to its own row.
                </div>
              </div>
            )}

            {/* Single flat table (single-date) OR one table per date-group */}
            {(isMultiDate ? parsed.groups : [{ date: parsed.header?.date, channel_name: parsed.header?.channel_name, trades: parsed.trades, total: parsed.total }]).map((group, groupIdx) => {
              const groupIdxForEdit = isMultiDate ? groupIdx : null;
              const gTrades = group.trades || [];
              const gComputed = gTrades.filter(t => t.valid).reduce((s, t) => s + (Number(t.pips) || 0), 0);
              return (
                <div key={groupIdx} className="border border-[hsl(var(--border))] rounded-lg overflow-hidden" data-testid={`preview-group-${groupIdx}`}>
                  {isMultiDate && (
                    <div className="px-3 py-2 bg-[hsl(var(--surface-elevated))]/60 border-b border-[hsl(var(--border))] flex items-center justify-between text-xs">
                      <span className="font-semibold text-cyan-300" data-testid={`group-date-${groupIdx}`}>
                        📅 {group.date} {group.channel_name && <span className="text-[hsl(var(--text-muted))] font-normal">— {group.channel_name}</span>}
                      </span>
                      <span className="text-[10px] text-[hsl(var(--text-muted))]">{gTrades.length} trade{gTrades.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[760px]">
                    <thead className="bg-[hsl(var(--surface-elevated))]">
                      <tr>
                        <th className="text-left px-2 py-2">#</th>
                        <th className="text-left px-2 py-2">Pair</th>
                        <th className="text-left px-2 py-2">Position</th>
                        <th className="text-left px-2 py-2">Status</th>
                        <th className="text-right px-2 py-2">Pips</th>
                        <th className="text-left px-2 py-2">Post To</th>
                        <th className="text-left px-2 py-2">Signal</th>
                        <th className="text-left px-2 py-2">Provider <span className="text-red-400">*</span></th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {gTrades.map((t, idx) => (
                        <tr key={idx} className={`border-t border-[hsl(var(--border))] ${!t.valid ? 'bg-red-500/5' : ''}`} data-testid={`trade-row-${groupIdx}-${idx}`}>
                          <td className="px-2 py-1.5 text-[hsl(var(--text-muted))]">{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            <Input value={t.pair || ''} onChange={(e) => handleEditTrade(idx, 'pair', e.target.value.toUpperCase(), groupIdxForEdit)} className="h-7 w-24 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid={`trade-pair-${groupIdx}-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={t.position || ''} onValueChange={(v) => handleEditTrade(idx, 'position', v, groupIdxForEdit)}>
                              <SelectTrigger className="h-7 w-20 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid={`trade-position-${groupIdx}-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                {POSITION_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={t.status || ''} onValueChange={(v) => handleEditTrade(idx, 'status', v, groupIdxForEdit)}>
                              <SelectTrigger className="h-7 w-28 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid={`trade-status-${groupIdx}-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <Input type="number" value={t.pips} onChange={(e) => handleEditTrade(idx, 'pips', e.target.value, groupIdxForEdit)} className={`h-7 w-20 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-right ${t.pips > 0 ? 'text-emerald-400' : t.pips < 0 ? 'text-red-400' : ''}`} data-testid={`trade-pips-${groupIdx}-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={t.post_to || 'both'} onValueChange={(v) => handleEditTrade(idx, 'post_to', v, groupIdxForEdit)}>
                              <SelectTrigger className="h-7 w-24 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid={`trade-post-to-${groupIdx}-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                <SelectItem value="public">Public</SelectItem>
                                <SelectItem value="vip">VIP</SelectItem>
                                <SelectItem value="both">Both</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={t.signal_generated ? t.signal_generated : '__blank__'} onValueChange={(v) => handleEditTrade(idx, 'signal_generated', normaliseSignal(v), groupIdxForEdit)}>
                              <SelectTrigger className="h-7 w-24 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid={`trade-signal-${groupIdx}-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                {SIGNAL_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={t.signal_provider || ''} onValueChange={(v) => handleEditTrade(idx, 'signal_provider', v, groupIdxForEdit)}>
                              <SelectTrigger className={`h-7 w-24 text-xs bg-[hsl(var(--surface))] ${t.signal_provider ? 'border-[hsl(var(--border))]' : 'border-amber-500/60'}`} data-testid={`trade-provider-${groupIdx}-${idx}`}><SelectValue placeholder="Pilih" /></SelectTrigger>
                              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                {SIGNAL_PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Button size="sm" variant="ghost" onClick={() => handleRemoveTrade(idx, groupIdxForEdit)} className="h-6 w-6 p-0 hover:bg-red-500/10 hover:text-red-400" data-testid={`trade-remove-${groupIdx}-${idx}`}>
                              <Trash2 size={11} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[hsl(var(--surface-elevated))]">
                      <tr>
                        <td colSpan={4} className="px-2 py-2 text-right font-semibold text-[hsl(var(--text-secondary))]">Computed Total:</td>
                        <td className={`px-2 py-2 text-right font-bold ${gComputed > 0 ? 'text-emerald-400' : gComputed < 0 ? 'text-red-400' : ''}`} data-testid={`computed-total-${groupIdx}`}>{gComputed} PIP</td>
                        <td colSpan={4}></td>
                      </tr>
                      {group.total?.stated != null && (
                        <tr>
                          <td colSpan={4} className="px-2 py-1 text-right text-[10px] text-[hsl(var(--text-muted))]">Stated in template:</td>
                          <td className={`px-2 py-1 text-right text-[10px] ${group.total.matches ? 'text-emerald-300' : 'text-amber-300'}`}>
                            {group.total.stated} PIP {group.total.matches ? <CheckCircle2 size={10} className="inline ml-1" /> : <AlertCircle size={10} className="inline ml-1" />}
                          </td>
                          <td colSpan={4}></td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                  </div>
                </div>
              );
            })}

            {/* Save destination */}
            <div className={`grid grid-cols-1 ${isMultiDate ? 'md:grid-cols-4' : 'md:grid-cols-5'} gap-3 bg-[hsl(var(--surface-elevated))]/40 border border-[hsl(var(--border))] rounded-lg p-3`}>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Channel</label>
                <Select value={overrideIbId} onValueChange={setOverrideIbId}>
                  <SelectTrigger className="h-8 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1" data-testid="save-channel-select"><SelectValue placeholder="Pick channel" /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    {activeChannels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {parsed.header?.channel_match_id && (
                  <span className="text-[9px] text-emerald-400">✓ Auto-matched from header</span>
                )}
              </div>
              {!isMultiDate && (
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Trade Date</label>
                  <Input type="date" value={overrideDate} onChange={(e) => setOverrideDate(e.target.value)} className="h-8 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1" data-testid="save-date-input" />
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Post to</label>
                <Select value={postTo} onValueChange={setPostTo}>
                  <SelectTrigger className="h-8 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1" data-testid="save-post-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    <SelectItem value="both">Both (VIP + Public)</SelectItem>
                    <SelectItem value="vip">VIP only</SelectItem>
                    <SelectItem value="public">Public only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Default Signal</label>
                <div className="flex items-center gap-1 mt-1">
                  <Select value={defaultSignal || '__blank__'} onValueChange={(v) => setDefaultSignal(normaliseSignal(v))}>
                    <SelectTrigger className="h-8 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] flex-1" data-testid="save-default-signal-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      {SIGNAL_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={applyDefaultSignalToAll} className="h-8 px-2 border-[hsl(var(--border))] text-[10px]" data-testid="apply-default-signal-btn" title="Apply this Signal Generated value to every row in the preview">
                    Apply All
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Signal Provider <span className="text-red-400">*</span></label>
                <Select value={defaultProvider} onValueChange={applyProviderToAll}>
                  <SelectTrigger className="h-8 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1" data-testid="save-provider-all-select"><SelectValue placeholder="Set semua baris" /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    {SIGNAL_PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-[9px] text-[hsl(var(--text-muted))]">Pilih sekali untuk semua baris, atau ubah setiap baris</span>
              </div>
            </div>
            {missingProvider > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded p-2 text-xs text-amber-300 flex items-start gap-2" data-testid="provider-missing-warning">
                <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {missingProvider} trade belum ada Signal Provider — wajib pilih sebelum simpan.
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[hsl(var(--border))]">
          <Button variant="outline" onClick={onClose} className="border-[hsl(var(--border))]">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"
            data-testid="save-trades-btn"
          >
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
            Save {validTrades.length} Trade{validTrades.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
